import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Auth } from 'aws-amplify';

/**
 * ChangePassword component for users who need to change their password
 * This is required for users created by admins (FORCE_CHANGE_PASSWORD status)
 */
const ChangePassword: React.FC = () => {
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingUser, setPendingUser] = useState<any>(null);
  const [initializing, setInitializing] = useState(true);

  // Get the user data from sessionStorage
  useEffect(() => {
    const pendingData = sessionStorage.getItem('pendingPasswordChange');
    if (pendingData) {
      try {
        const data = JSON.parse(pendingData);
        setPendingUser(data);
      } catch (err) {
        console.error('Error parsing pending password change data:', err);
        setError('Session expired. Please login again.');
      }
    } else {
      setError('No pending password change. Please login again.');
    }
    setInitializing(false);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (!/[A-Z]/.test(newPassword)) {
      setError('Password must contain at least one uppercase letter');
      return;
    }

    if (!/[a-z]/.test(newPassword)) {
      setError('Password must contain at least one lowercase letter');
      return;
    }

    if (!/[0-9]/.test(newPassword)) {
      setError('Password must contain at least one number');
      return;
    }

    if (!/[^A-Za-z0-9]/.test(newPassword)) {
      setError('Password must contain at least one special character');
      return;
    }

    try {
      setLoading(true);

      if (!pendingUser || !pendingUser.username) {
        setError('Session expired. Please login again.');
        navigate('/login');
        return;
      }

      // Get the temporary password from sessionStorage
      const tempPassword = sessionStorage.getItem('tempPassword');
      if (!tempPassword) {
        setError('Session expired. Please login again.');
        navigate('/login');
        return;
      }

      // Sign in with temporary password to get the user object with challenge
      const user = await Auth.signIn(pendingUser.username, tempPassword);

      // Complete the new password challenge with required attributes
      await Auth.completeNewPassword(user, newPassword, {
        name: user.challengeParam?.userAttributes?.name || pendingUser.username,
      });

      // Clear the session storage
      sessionStorage.removeItem('pendingPasswordChange');
      sessionStorage.removeItem('tempPassword');

      // Password changed successfully, redirect to dashboard
      navigate('/dashboard');
    } catch (err: any) {
      console.error('Error changing password:', err);
      setError(err.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  // Show loading while initializing
  if (initializing) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ marginTop: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // If no user object, show error
  if (!pendingUser) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ marginTop: 8 }}>
          <Paper elevation={3} sx={{ padding: 4 }}>
            <Alert severity="error">{error || 'Session expired. Please login again.'}</Alert>
            <Button fullWidth variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/login')}>
              Go to Login
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            Change Password
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            You must change your password before continuing
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              name="newPassword"
              label="New Password"
              type="password"
              id="newPassword"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              helperText="Must be at least 8 characters with uppercase, lowercase, number, and special character"
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              id="confirmPassword"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? 'Changing Password...' : 'Change Password'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ChangePassword;
